<template>
    <sub-section title="Apply" :showHelp="false">
        <p>
            We need to collect some more <span @dblclick="onInfoDoubleClick">information</span>. With your permission we will then provide this 
            to Pathlines to open your {{ Constants.Product.Name }} product and transfer any monies in.
        </p>
        <p class="fw-bold">
            Pathlines cannot provide you with advice or a recommendation. If you want a recommendation you must seek independent financial advice.
        </p>

        <div class="form-group">
            <div class="form-floating">
                <dropdown :items="['Miss', 'Mr', 'Mrs', 'Ms']" v-model="applicationDetail.title" :optional="false" 
                    description="Title" abbreviated-description="Title" classes="form-select form-control">
                </dropdown>
            </div>
        </div>
        <div class="form-floating mb-1">
            <input v-model="applicationDetail.firstName" type="text" id="firstName" class="form-control form-control-grouped" maxlength="25" autocomplete="given-name">
            <label for="floatingInput" :class="{ 'text-primary': false }">First name</label>
        </div>
        <div class="form-floating mb-1">
            <input v-model="applicationDetail.middleNames" type="text" id="middleNames" class="form-control form-control-grouped" maxlength="75">
            <label for="floatingInput" :class="{ 'text-primary': false }">Any middle names</label>
        </div>
        <div class="form-floating mb-1">
            <input v-model="applicationDetail.lastName" type="text" id="lastName" class="form-control form-control-grouped" maxlength="25" autocomplete="family-name">
            <label for="floatingInput" :class="{ 'text-primary': false }">Last name</label>
        </div>
        <div class="form-group">
            <div class="form-floating">
                <dropdown :items="nationalityDropdownData" v-model="applicationDetail.nationality" :optional="false" 
                    description="Nationality" abbreviated-description="Nationality" classes="form-select form-control mt-0 mb-4">
                </dropdown>
            </div>
        </div>

        <div class="form-floating mb-1">
            <input v-model="applicationDetail.houseNumber" type="text" class="form-control" maxlength="25">
            <label for="floatingInput" :class="{ 'text-primary': false }">House number or name</label>
        </div>
        <div class="form-floating mb-1">
            <input v-model="applicationDetail.street" type="text" class="form-control form-control-grouped" maxlength="50">
            <label for="floatingInput" :class="{ 'text-primary': false }">Street</label>
        </div>
        <div class="form-floating mb-1">
            <input v-model="applicationDetail.townOrCity" type="text" id="city" class="form-control form-control-grouped" maxlength="75" autocomplete="address-level2">
            <label for="floatingInput" :class="{ 'text-primary': false }">Town/City</label>
        </div>
        <div class="form-floating mb-1">
            <input v-model="applicationDetail.postCode" type="text" id="postalCode" class="form-control form-control-grouped" maxlength="10" autocomplete="postal-code">
            <label for="floatingInput" :class="{ 'text-primary': false }">Postcode</label>
        </div>
        <div class="form-group">
            <div class="form-floating">
                <dropdown :items="countriesDropdownData" v-model="applicationDetail.country" :optional="false" 
                    description="Country" abbreviated-description="Country" classes="form-select form-control mt-0">
                </dropdown>
            </div>
        </div>

        <div class="form-group">
            <div class="form-floating">
                <dropdown :items="mobileCountryDropdownData" v-model="applicationDetail.mobileCountryCode" :optional="false"
                    description="Mobile country code" abbreviated-description="Mobile country code" classes="form-select form-control mt-4">
                </dropdown>
            </div>
        </div>
        <div class="form-floating mb-4">
            <input v-model="applicationDetail.mobile" type="text" id="tel" class="form-control mt-1" maxlength="20" autocomplete="tel">
            <label for="floatingInput" :class="{ 'text-primary': false }">Mobile</label>
        </div>

        <div class="container mt-4">
            <div class="row align-items-center">
                <div class="col-md-9 ps-0">
                    <p>
                        When you click the {{ Constants.Product.Name }} button, you are consenting to Guiide passing
                        your pension information to Pathlines Pensions UK Limited. This will direct you into the
                        Pathlines website and application process where you will apply for a Pathlines Pensions
                        UK Limited product. Guiide will share the information you have provided including your
                        income plan and the investment selection modelled with us.
                    </p>
                </div>
                <div class="col-md-3 d-flex justify-content-center align-items-center">
                    <div class="text-end">
                        <button type="button" @click="onApplyClicked" :disabled="isApplying" class="btn px-5 btn-primary">
                            {{ Constants.Product.Name }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <dynamic-advice-viewer :message="adviceMessage"></dynamic-advice-viewer>
    </sub-section>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed, reactive } from "vue";
import { app } from "@/guiide/ts/app";
import { AdditionalPersonalInfo } from "@/guiide/ts/additionalPersonalInfo";
import { Country } from "@/guiide/ts/country";
import Result from "@/guiide/ts/result";
import { PropertyInfo } from "@/guiide/ts/propertyInfo";
import SubSection from "../common/SubSection.vue";
import DynamicAdviceViewer from "../common/DynamicAdviceViewer.vue";
import Dropdown from "../common/Dropdown.vue";
import Constants from "@/guiide/ts/constants";

const props = defineProps({
    isApplying: { type: Boolean, default: false },
    adviceMessage: { type: String, default: "" }
});

const emit = defineEmits<{ (e: 'apply') }>();

defineExpose({ validateAdditionalInfo, getAdditionalInfo });

class ApplicationInfo {
    title: string;
    firstName: string;
    middleNames: string;
    lastName: string;
    houseNumber: string;
    street: string;
    townOrCity: string;
    postCode: string;
    country: string;
    nationality: string;
    mobileCountryCode: string;
    mobile: string;
}

const additionalInfoPropertyInfo: Record<keyof ApplicationInfo, PropertyInfo> = {
  title: new PropertyInfo("title"),
  firstName: new PropertyInfo("first name"),
  middleNames: new PropertyInfo("middle names", true),
  lastName: new PropertyInfo("last name"),
  houseNumber: new PropertyInfo("house number or name"),
  street: new PropertyInfo("street"),
  townOrCity: new PropertyInfo("town or city"),
  postCode: new PropertyInfo("postcode"),
  country: new PropertyInfo("country"),
  nationality: new PropertyInfo("nationality"),
  mobileCountryCode: new PropertyInfo("mobile country code"),
  mobile: new PropertyInfo("mobile number"),
};

const alreadyInDrawdown = ref("No");
const pensionsAlreadyInDrawdownWarning = ref("");
const countries = ref<Array<Country>>(new Array<Country>());

onMounted(async () => {
    const countriesData = await app.getCountries();
    countries.value = countriesData;
});

const applicationDetail: ApplicationInfo = reactive<ApplicationInfo>({
    title: "",
    firstName: "",
    middleNames: "",
    lastName: "",
    houseNumber: "",
    street: "",
    townOrCity: "",
    postCode: "",
    country: "United Kingdom",
    nationality: "British",
    mobileCountryCode: "+44 (United Kingdom)",
    mobile: ""
});

watch(
    () => alreadyInDrawdown.value,
    (newValue: string) => {
        pensionsAlreadyInDrawdownWarning.value = newValue == "Yes"
            ? "Unfortunately we're unable to transfer pensions that are already in drawdown. Please ensure any pensions already in drawdown are not selected in your transfer list above."
            : "";
    }
)

const countriesDropdownData = computed<Array<string>>(() => {
    return countries?.value?.map(c => c.name) ?? new Array<string>();
});

const nationalityDropdownData = computed<Array<string>>(() => {
    return countries?.value?.map(c => c.nationality) ?? new Array<string>();
})

const mobileCountryDropdownData = computed<Array<string>>(() => {
    return countries?.value?.map(c => `${c.dialingCode} (${c.name})`) ?? new Array<string>();
})

async function onApplyClicked() {
    await emit('apply');
}

function validateAdditionalInfo(): Result {
    for (const key in applicationDetail) {
        if (Object.prototype.hasOwnProperty.call(applicationDetail, key)) {
            const value = applicationDetail[key as keyof ApplicationInfo];
            const propertyInfo = additionalInfoPropertyInfo[key as keyof ApplicationInfo];

            if (value === "" && !propertyInfo.optional) {
                return new Result(false, `Please tell us your ${propertyInfo.referenceName}.`);
            }
        }
    }

    return new Result(true);
}

function getAdditionalInfo(): AdditionalPersonalInfo {
    const nationalityCountry = countries.value.find(c => c.nationality == applicationDetail.nationality);
    const addressCountry = countries.value.find(c => c.name == applicationDetail.country);
    const mobileCountryCodeElements = applicationDetail.mobileCountryCode.split(' ');
    const mobileCountryDialingCode = mobileCountryCodeElements[0];
    const mobileCountry = countries.value.find(c => c.dialingCode == mobileCountryDialingCode);

    return {
        title: applicationDetail.title,
        firstName: applicationDetail.firstName,
        middleNames: applicationDetail.middleNames,
        lastName: applicationDetail.lastName,
        nationality: nationalityCountry.isoCode,
        address: {
            houseNumber: applicationDetail.houseNumber,
            street: applicationDetail.street,
            townOrCity: applicationDetail.townOrCity,
            postCode: applicationDetail.postCode,
            country: addressCountry.isoCode
        },
        mobileCountryCode: mobileCountry.isoCode,
        mobile: applicationDetail.mobile
    }
}

function onInfoDoubleClick() {
    Object.assign(applicationDetail, {
        title: "Mr",
        firstName: "John",
        lastName: "Walker",
        houseNumber: "45",
        street: "Hillview Road",
        townOrCity: "Bath",
        postCode: "BA34 9KS",
        mobile: "7753 453243"
    });
}

</script>

<style scoped>
.form-control-grouped {
    margin-top: 5px;
}
</style>